// Pure MP-rung -> pricer-spec mapping (IM-1955). No React, no WS — unit-tested
// in __tests__/handoff.test.ts. The card calls buildHandoffSpec on a model-cell
// click and hands the result to openInPricer.
import type { TouchDirection } from './ladder';
import type { PricerHandoffSpec } from '../../providers/pricer-handoff/pricer-handoff-provider';

// Card-level context the rung mapping needs beyond the rung itself: the market
// token, the selected expiry ISO, and the board-wide settlement (pay_time /
// pay_token) resolved on SettlementInfo.
export interface HandoffCardContext {
  // Registry-resolved base_quote the pricer locks/prices on (e.g. 'XRP_USDC').
  token: string;
  // MP display token for the tab name (e.g. 'XRP').
  displayToken: string;
  // The MP expiry ISO — "YYYY-MM-DD 08:00:00" (month_end) or an exchange date.
  expiryIso: string;
  // settlement.onTouch: pay_time === 'at_touch'.
  onTouch: boolean;
  // settlement.inQuote: pay_token is the quote ccy (not a listed base token).
  inQuote: boolean;
}

// The pricer instrument row stores a bare "YYYY-MM-DD" (ExpiryCell reads
// inst.expiry.slice(0,10)); MP expiries carry a "HH:MM:SS" tail on the
// month_end family. Slice to the date — this is the fiddly round-trip bit.
export const toPricerExpiry = (iso: string): string => iso.slice(0, 10);

// Seed notional for a handed-off one-touch. A small unit notional (NOT the
// pricer's 100k default seed) so the row prices instantly and the trader scales
// it after landing — the handoff carries the STRUCTURE, size is theirs to set.
export const HANDOFF_DEFAULT_NOTIONAL = 100;

export function buildHandoffSpec(
  rung: { level: number; direction: TouchDirection },
  model: 'bs' | 'lv' | 'sv',
  ctx: HandoffCardContext,
): PricerHandoffSpec {
  return {
    token: ctx.token,
    displayToken: ctx.displayToken,
    // direction up -> OneTouchUpper, down -> OneTouchLower. The barrier is the
    // rung level either way; the contract schema decides upper vs lower field.
    contract: rung.direction === 'upper' ? 'OneTouchUpper' : 'OneTouchLower',
    barrier: rung.level,
    // pay_time maps 1:1 onto the pricer PayType enum.
    payType: ctx.onTouch ? 'at_touch' : 'at_expiry',
    // pay_token (base / USD-quote) -> notionalToken.
    notionalToken: ctx.inQuote ? 'quote' : 'base',
    expiry: toPricerExpiry(ctx.expiryIso),
    model,
    notional: HANDOFF_DEFAULT_NOTIONAL,
  };
}
